<template>
  <div class="space-y-6">
    <!-- KPI Cards -->
    <div class="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Jobs</p>
        <p class="text-2xl font-bold text-gray-900 mt-1">{{ summary.total || 0 }}</p>
        <p class="text-xs text-gray-400 mt-1">All jobs in selected period</p>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Meters</p>
        <p class="text-2xl font-bold text-gray-900 mt-1">{{ totalMeters.toLocaleString() }}</p>
        <p class="text-xs text-gray-400 mt-1">Combined meters across all regions</p>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Completion Rate</p>
        <p class="text-2xl font-bold mt-1" :class="completionRate >= 80 ? 'text-green-600' : completionRate >= 50 ? 'text-yellow-600' : 'text-red-600'">{{ completionRate }}%</p>
        <p class="text-xs text-gray-400 mt-1">Completed vs total jobs</p>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Cancelled &amp; Lost</p>
        <p class="text-2xl font-bold mt-1" :class="cancelledLostRate <= 10 ? 'text-green-600' : cancelledLostRate <= 30 ? 'text-yellow-600' : 'text-red-600'">{{ cancelledLostRate }}%</p>
        <p class="text-xs text-gray-400 mt-1">{{ cancelledLost.cancelledAndLostJobs || 0 }} of {{ cancelledLost.totalCancelled || 0 }} cancelled never re-won</p>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Testers</p>
        <p class="text-2xl font-bold text-gray-900 mt-1">{{ activeTesterCount }}</p>
        <p class="text-xs text-gray-400 mt-1">Assigned testers with activity</p>
      </div>
    </div>

    <!-- Charts -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Jobs Over Time -->
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <h3 class="text-sm font-semibold text-gray-700">Jobs Over Time</h3>
        <p class="text-xs text-gray-400 mb-4">Daily breakdown of total, completed, and cancelled jobs</p>
        <apexchart
          v-if="areaChartSeries[0].data.length"
          type="area"
          height="280"
          :options="areaChartOptions"
          :series="areaChartSeries"
        />
        <div v-else class="h-64 flex items-center justify-center text-sm text-gray-400">No data for this period</div>
      </div>

      <!-- Jobs by Status -->
      <div class="bg-white rounded-xl border border-gray-200 p-5">
        <h3 class="text-sm font-semibold text-gray-700">Jobs by Status</h3>
        <p class="text-xs text-gray-400 mb-4">Proportion of jobs across each status</p>
        <apexchart
          v-if="donutSeries.length && donutSeries.some(v => v > 0)"
          type="donut"
          height="280"
          :options="donutOptions"
          :series="donutSeries"
        />
        <div v-else class="h-64 flex items-center justify-center text-sm text-gray-400">No data for this period</div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    summary: { type: Object, default: () => ({}) },
    jobsOverTime: { type: Array, default: () => [] },
    regionMetrics: { type: Array, default: () => [] },
    testers: { type: Array, default: () => [] },
    cancelledLost: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  computed: {
    totalMeters() {
      if (this.regionMetrics.length) {
        return this.regionMetrics.reduce((sum, r) => sum + (r.meters || 0), 0);
      }
      return 0;
    },
    completionRate() {
      const total = this.summary.total || 0;
      const completed = (this.summary.byStatus && this.summary.byStatus["COMPLETED"]) || 0;
      return total ? Math.round((completed / total) * 100) : 0;
    },
    cancelledLostRate() {
      return this.cancelledLost.rate || 0;
    },
    activeTesterCount() {
      return this.testers.filter(t => t.name !== "Unassigned").length;
    },
    areaChartSeries() {
      return [
        { name: "Total", data: this.jobsOverTime.map(d => ({ x: d.date, y: d.total })) },
        { name: "Completed", data: this.jobsOverTime.map(d => ({ x: d.date, y: d.completed })) },
        { name: "Cancelled", data: this.jobsOverTime.map(d => ({ x: d.date, y: d.cancelled })) },
      ];
    },
    areaChartOptions() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ["#293a4c", "#01BC38", "#ef4444"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.05 } },
        xaxis: { type: "datetime", labels: { style: { fontSize: "11px", colors: "#9ca3af" } } },
        yaxis: { labels: { style: { fontSize: "11px", colors: "#9ca3af" } } },
        grid: { borderColor: "#f3f4f6" },
        legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
        tooltip: { x: { format: "dd MMM yyyy" } },
      };
    },
    donutSeries() {
      if (!this.summary.byStatus) return [];
      const statuses = ["COMMITTED", "COMPLETED", "ON-CALL", "CANCELLED", "FIRM"];
      return statuses.map(s => this.summary.byStatus[s] || 0);
    },
    donutOptions() {
      return {
        labels: ["Committed", "Completed", "On-Call", "Cancelled", "Firm"],
        colors: ["#3b82f6", "#01BC38", "#f59e0b", "#ef4444", "#8b5cf6"],
        chart: { toolbar: { show: false } },
        legend: { position: "bottom", fontSize: "12px" },
        dataLabels: { enabled: true, formatter: (val) => Math.round(val) + "%" },
        plotOptions: { pie: { donut: { size: "55%" } } },
      };
    },
  },
};
</script>
